import { Helmet } from "react-helmet";
import { SideBar } from "../side-bar/SideBar";
import { employeeDataProps } from "../../../utils/types";
import React, { useEffect, useState, MouseEvent, ChangeEvent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faUserShield, faPlus, faPaperPlane, faBoxArchive, faArrowRotateLeft } from "@fortawesome/free-solid-svg-icons";
import Modal from "../modal/modal";
import AddUserChildren from "../modal/manage-user-modal/addUser";
import UpdateUser from "../modal/manage-user-modal/updateUser";
import { AxiosError } from "axios";
import * as APIURL from "../../../config/config";
import { api, getToken } from "../../../services/apis/auth-api/apiUrl";
import { Notification } from "../pop-ups/notification";
import UpdatePrivilege from "../modal/manage-user-modal/updatePrivilege";

export default function ManageUsers (){
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalChildren, setModalChildren] = useState<React.JSX.Element | null>(null);
    const [accountData, setAccountData] = useState<employeeDataProps[]>();
    const [users, setUsers] = useState<employeeDataProps[]>();
    const [showNotif, setShowNotif] = useState<boolean>(false);
    const [notifValue, setNotifValue] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>('');
    const [toLoadUsers, setToLoadUsers] = useState<string>('active');

    useEffect(()=>{
        async function getEmployees () {
            try{

                let end_point;

                toLoadUsers === 'active' ? end_point = APIURL.GET_EMPLOYEE_END_POINT : end_point = APIURL.GET_ARCHIVED_USERS_END_POINT;

                const results = await api.get(end_point, {
                    headers : {'Authorization' : 'Bearer '+getToken()}
                })

                setAccountData(results.data);
                setIsLoading(false);
            }catch(e){
                const error = (e as AxiosError);
                setIsLoading(false);
                error.response?.status === 401 ? alert('Unauthorized access!') : alert('Something went wrong!');
            }
        }
        getEmployees();
    },[showModal, showNotif, toLoadUsers])

    useEffect(()=>{
        const setSearchFilter = () => {
            const filtered = accountData?.filter((user)=>
                user.last_name.toLowerCase().includes(search.toLowerCase()) ||
                user.first_name.toLowerCase().includes(search.toLowerCase()) ||
                user.email.toLowerCase().includes(search.toLowerCase())
            );

            setUsers(filtered);
        }
        setSearchFilter();
    }, [accountData, search]);

    const handleFilter = (e : ChangeEvent<HTMLSelectElement>) => {
        setToLoadUsers(e.target.value);
        setIsLoading(true);
    }
    function createModalContent(content : React.JSX.Element){
        setModalChildren(content);
        setShowModal(true);
    }

    async function resendSignUpLinkEmail(email : string, e : MouseEvent<HTMLButtonElement>){
        e.preventDefault();

        setShowNotif(true);
        setNotifValue('Sending....');
        try{
            await api.get(APIURL.RESEND_SIGNUP_EMAIL_END_POINT+email, {
                headers : {'Authorization' : 'Bearer '+getToken()}
            });
            setIsSuccess(true);
            setNotifValue('Signup email sent!');
        }catch(e){
            const error = (e as AxiosError);
            setIsSuccess(false);
            if(error.response?.status === 401){
                setNotifValue('Session has expired!');
            }else{
                setNotifValue('Email not sent! Please try again.');
            }
        }
        
        setTimeout(()=>{
            setIsSuccess(null)
            setShowNotif(false)
        },1000)        
    }

    function archiveUser (id : number, e : MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        setShowNotif(true);
        setNotifValue('Processing....');

        setTimeout(async()=>{
            try{
                await api.get(APIURL.ARCHIVE_USER_END_POINT+id, {
                    headers : {'Authorization' : 'Bearer '+getToken()}
                });
                setIsSuccess(true);
                setNotifValue('User was archived.');
            }catch(e){
                const error = (e as AxiosError);
                setIsSuccess(false);
                if(error.response?.status === 401) {
                    setNotifValue('Session has expired!');
                }else{
                    setNotifValue('An error occured!');
                }
            }

            setTimeout(()=>{
                setIsSuccess(null)
                setShowNotif(false)
            },1000)    
        },500)
    }

    function recoverUser (id : number, e : MouseEvent<HTMLButtonElement>) {
        e.preventDefault();

        setShowNotif(true);
        setNotifValue('Processing....');

        setTimeout(async()=>{
            try{
                await api.get(APIURL.RECOVER_USERS_END_POINT+id, {
                    headers : {'Authorization' : 'Bearer '+getToken()}
                });
                setIsSuccess(true);
                setNotifValue('User was recovered.');
            }catch(e){
                const error = (e as AxiosError);
                setIsSuccess(false);
                if(error.response?.status === 401) {
                    setNotifValue('Session has expired!');
                }else{
                    setNotifValue('An error occured!');
                }
            }

            setTimeout(()=>{
                setIsSuccess(null)
                setShowNotif(false)
            },1000)    
        },500)
    }

    return (
        <div className="content flex">
            <Helmet>
                <title>Manage Users</title>
            </Helmet>

            <SideBar />
            
            <div className='main-content'>

                <div className='w-full mt-10 py-4 px-4 flex bg-white border mb-2'>
                    <div className='ms-auto flex gap-2 items-center'>
                        <p className="uppercase text-gray-500">
                            Filter:
                        </p>
                        <select onChange={handleFilter} className="text-box text-gray-500">
                            <option value="active">Active</option>
                            <option value="removed">Archived</option>
                        </select>
                    </div>
                </div>
                
                <div className='w-full py-4 px-4 flex bg-white border mb-2'>
                    <div>
                        <input type="text" className='text-box' onChange={(e)=>setSearch(e.target.value)} value={search} placeholder="Search users...." />
                    </div>
                    <div className='ms-auto'>
                        <button type="button" onClick={()=> createModalContent(<AddUserChildren />)} className='button button-vcc-primary px-4 text-sm'>
                            <FontAwesomeIcon icon={faPlus} />
                            Add User
                        </button>
                    </div>
                </div>

                <div className='max-h-[30rem] verflow-y-auto overflow-x-auto hide-scrollbar border bg-white'>
                    <table className="w-full border">
                        <thead className="table-fixed-header border">
                            <tr>
                                <th className='table-headers'>#</th>
                                <th className='table-headers'>Full name</th>
                                <th className='table-headers'>Email</th>
                                <th className='table-headers'>Status</th>
                                <th className='table-headers'>Date Created</th>
                                <th className='table-headers'>Action</th>
                            </tr>
                        </thead>
                        <tbody className="text-center">
                            {isLoading ? (
                                <>
                                    <tr>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                    </tr>
                                    <tr>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                        <td className='table-body px-2'><div className="table-loading"></div></td>
                                    </tr>
                                </>
                            ) : (
                                <>
                                    {accountData && accountData?.length > 0 ? (
                                        <>
                                            {users?.map((account, index)=>(
                                                <tr className="hover:bg-gray-300">
                                                    <td className='table-body px-2'>{index + 1}</td>
                                                    <td className='table-body px-2'>{account.first_name} {account.last_name}</td>
                                                    <td className='table-body px-2'>{account.email}</td>
                                                    <td className='table-body px-2'>{account.status}</td>
                                                    <td className='table-body px-2'>{account.date_created}</td>
                                                    <td className='table-body px-2'>
                                                        <div className='w-full flex gap-4 justify-center'>
                                                            {account.status === 'removed' ? (
                                                                <>
                                                                    <button type="button" onClick={(e)=>recoverUser(account.id, e)} className='text-blue-400'>
                                                                        <FontAwesomeIcon icon={faArrowRotateLeft} />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button type="button" onClick={()=>{createModalContent(<UpdateUser id={account.id} />)}} className='text-blue-400'>
                                                                        <FontAwesomeIcon icon={faPenToSquare} />
                                                                    </button>
                                                                    <button type="button" onClick={()=>createModalContent(<UpdatePrivilege id={account.id} name={`${account.first_name} ${account.last_name}`} />)} className='text-gray-500'>
                                                                        <FontAwesomeIcon icon={faUserShield} />
                                                                    </button>
                                                                    <button type="button" onClick={(e)=>archiveUser(account.id, e)} className='text-gray-500'>
                                                                        <FontAwesomeIcon icon={faBoxArchive} />
                                                                    </button>
                                                                    {account.status === 'unverified' &&(
                                                                        <button type="button" onClick={(e) =>resendSignUpLinkEmail(account.email, e)} className='text-gray-500'>
                                                                            <FontAwesomeIcon icon={faPaperPlane} />
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            <tr className="hover:bg-gray-300">
                                                <td colSpan={6} className='table-body px-2'>
                                                    No users found.
                                                </td>
                                            </tr>
                                        </>
                                    )}
                                </>
                            )}
                            </tbody>
                    </table>
                </div>

            </div>
             {showModal && (
                <Modal onClose={()=>{setShowModal(false)}}> 
                    {modalChildren}
                </Modal>
             )}
             {showNotif && (
                <Notification notif={notifValue} success={isSuccess} />
             )}
        </div>
    )
}